import {
  type HackathonWithState,
  type HackathonWinnerGroup,
  type Hackathon,
  hackathonProjectAndTeamSchema,
  projectMediaSchema,
  projectSurveySchema,
  githubRepoRegex,
} from '../lib';
import { z } from 'zod';

export const hackathonRegistrationSchema = z.object({
  fromOnboarding: z.boolean().optional(),
  country: z.string().optional(),
  city: z.string().optional(),
  optOut: z.boolean().optional(),
});

export type HackathonRegistrationRequest = z.infer<
  typeof hackathonRegistrationSchema
>;

export type HackathonRegistrationResponse = {
  message: string;
};

export type RetrieveHackathonStateResponse = {
  hackathon: HackathonWithState;
};

export const githubRepoRegexSchema = z
  .string()
  .trim()
  .regex(githubRepoRegex, { message: 'Invalid GitHub repository link.' });

export const checkGithubRepoAccessSchema = z.object({
  repoLink: githubRepoRegexSchema,
});

export type CheckGithubRepoAccessRequest = z.infer<
  typeof checkGithubRepoAccessSchema
>;

export type GithubRepoStatus =
  | 'public'
  | 'private-with-access'
  | 'private-invite-pending'
  | 'private-no-access'
  | 'not-found';

export type CheckGithubRepoAccessResponse = {
  status: GithubRepoStatus;
  error?: string;
};

export type LeaveTeamResponse = {
  message: string;
  projectId: number;
};

export interface RetrieveHallOfFameResponse {
  hackathons: {
    hackathon: Hackathon;
    prizeGroups: HackathonWinnerGroup[];
  }[];
}

export const createHackathonProjectSchema =
  hackathonProjectAndTeamSchema.merge(projectMediaSchema);

export type CreateHackathonProjectBody = z.infer<
  typeof createHackathonProjectSchema
>;

export type CreateHackathonProjectResponse = {
  message: string;
};

export const editHackathonProjectSchema = createHackathonProjectSchema;

export type EditHackathonProjectBody = z.infer<
  typeof editHackathonProjectSchema
>;

export type EditHackathonProjectResponse = {
  message: string;
};

export const submitHackathonProjectSchema = projectSurveySchema.refine(
  (data) =>
    data.marketingChannels.length > 0 || data.marketingChannelOther !== '',
  {
    message: 'At least one marketing channel is required.',
    path: ['marketingChannels'],
  },
);

export type SubmitHackathonProjectBody = z.infer<
  typeof submitHackathonProjectSchema
>;

export type SubmitHackathonProjectResponse = {
  message: string;
};

export const retrieveSocialShareableImageSchema = z.object({
  username: z.string().optional(),
});

export type RetrieveSocialShareableImageQuery = z.infer<
  typeof retrieveSocialShareableImageSchema
>;

export type RetrieveSocialShareableImageResponse = {
  id: number;
  url: string;
  username: string;
  viewedAt: Date | null;
};

export const markSocialImageViewedSchema = z.object({
  id: z.number(),
});

export type MarkSocialImageViewedBody = z.infer<
  typeof markSocialImageViewedSchema
>;

export const submitHackathonWeeklyUpdateBodySchema = z.object({
  videoUrl: z
    .string()
    .min(1, { message: 'Video URL is required.' })
    .url('Please provide a valid full URL')
    .refine(
      (url) => {
        const lowerUrl = url.toLowerCase();
        return (
          lowerUrl.includes('youtube.com') ||
          lowerUrl.includes('youtu.be') ||
          lowerUrl.includes('loom.com') ||
          lowerUrl.includes('vimeo.com')
        );
      },
      {
        message: 'Only YouTube, Loom, and Vimeo links are allowed',
      },
    ),
});

export type SubmitHackathonWeeklyUpdateRequest = z.infer<
  typeof submitHackathonWeeklyUpdateBodySchema
>;

export type SubmitHackathonWeeklyUpdateResponse = {
  success: boolean;
};
